import { Fragment } from 'react';

import { tailint } from '@pukka/common/utils';
import { Loader } from '@pukka/ui-components';
import { RiCloseCircleFill } from 'react-icons/ri';
import { twMerge } from 'tailwind-merge';

interface TableCardCommonProps {
  readonly data: {
    title: string | React.ReactNode;
    sub: React.ReactNode[];
  }[];
}

interface TableCardHeaderProps {
  readonly variant: 'header';
}

interface TableCardBodyProps {
  readonly variant: 'body';
  readonly id: number;
  readonly isLoading?: boolean;
  readonly removable?: boolean;
  readonly onClickRemove?: (id: number) => void;
}

type TableCardProps = TableCardCommonProps & (TableCardHeaderProps | TableCardBodyProps);

const baseStyle = tailint({
  container: 'h-[426px] w-[238px] min-w-[238px] rounded border-[0.5px] text-sm',
  title: 'h-[50px] border-b px-2 py-[14px]',
  subTitle: 'px-2 py-2.5 text-neutral-500',
});

const variantStyle = tailint({
  header: {
    container: 'border-light-blue-300 pb-4',
    title: 'border-light-blue-500 bg-light-blue-100 font-bold',
  },
  body: {
    container: 'border-neutral-400',
    title: 'border-neutral-200 bg-neutral-200 text-center font-medium',
  },
});

function TableCard(props: TableCardProps) {
  const { data, variant, removable = false, isLoading, onClickRemove, id } = props as TableCardProps & TableCardBodyProps;

  return (
    <div
      data-testid={`table-card-${variant}`}
      className={`relative h-[426px] w-[238px] min-w-[238px] border-light-blue-300 ${isLoading ? 'animate-pulse bg-neutral-50' : ''}`}
    >
      {removable && (
        <RiCloseCircleFill
          data-testid="remove-btn"
          size={24}
          className="absolute -left-3 -top-3 cursor-pointer text-red-500"
          onClick={onClickRemove?.bind({}, id)}
        />
      )}
      <div className={twMerge(baseStyle.container, variantStyle[variant].container)}>
        {isLoading ? (
          <div className="flex h-full flex-auto items-center justify-center">
            <Loader />
          </div>
        ) : (
          data.map(({ title, sub }, hIndex) => {
            const isFirstTitle = hIndex === 0;

            return (
              <Fragment key={hIndex}>
                <div
                  data-testid={isFirstTitle ? 'table-card-title' : ''}
                  className={twMerge(
                    baseStyle.title,
                    variantStyle[variant].title,
                    isFirstTitle ? 'rounded-t' : 'border-t',
                    !title && 'bg-transparent',
                  )}
                >
                  {title}
                </div>
                {sub.map((subTitle, sIndex) => {
                  const isNotLastSubTitle = sIndex < sub.length - 1;
                  const isSalesData = variant === 'body' && hIndex < data.length - 1;

                  return (
                    <div
                      key={sIndex}
                      data-testid={isSalesData ? 'sales-data' : ''}
                      className={twMerge(
                        baseStyle.subTitle,
                        isNotLastSubTitle && 'border-b border-neutral-100',
                        isSalesData && 'text-center',
                      )}
                    >
                      {subTitle}
                    </div>
                  );
                })}
              </Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}

export { TableCard };
